const BASE_URL = 'https://api.transcirox.com/api/admin';

async function apiClient(endpoint, { body, ...customConfig } = {}) {
  const token = localStorage.getItem('admin_token');
  
  const headers = {
    'Accept': 'application/json',
  };
  
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };
  
  if (body) {
    config.body = body instanceof FormData ? body : JSON.stringify(body);
  }
  
  console.log("Sending request to:", `${BASE_URL}/${endpoint}`, config);
  
  const response = await fetch(`${BASE_URL}/${endpoint}`, config);
  
  if (response.status === 401) {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin/login';
    return Promise.reject(new Error('Unauthorized'));
  }
  
  const data = await response.json();
  
  if (response.ok) {
    return data;
  } else {
    return Promise.reject(data);
  }
}

function buildQuery(params = {}) {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : '';
}

export const api = {
  get: (endpoint, { params, ...config } = {}) =>
    apiClient(`${endpoint}${buildQuery(params)}`, { ...config, method: 'GET' })
      .then(data => ({ success: true, data }))
      .catch(error => ({ success: false, message: error.message || 'Request failed' })),
  
  post: (endpoint, body, config = {}) =>
    apiClient(endpoint, { ...config, method: 'POST', body })
      .then(data => ({ success: true, data }))
      .catch(error => ({ success: false, message: error.message || 'Request failed' })),
  
  put: (endpoint, body, config = {}) =>
    apiClient(endpoint, { ...config, method: 'PUT', body })
      .then(data => ({ success: true, data }))
      .catch(error => ({ success: false, message: error.message || 'Request failed' })),
  
  delete: (endpoint, config = {}) =>
    apiClient(endpoint, { ...config, method: 'DELETE' })
      .then(data => ({ success: true, data }))
      .catch(error => ({ success: false, message: error.message || 'Request failed' })),
};

// Auth APIs
export const authAPI = {
  login: (data) => api.post('login', data),
  getProfile: () => api.get('get-profile'),
  changePassword: (data) => api.post('change-password', data),
  forgotPassword: (data) => api.post('forgot-password', data),
  resetPassword: (data) => api.post('reset-password', data),
  updateProfile: (data) => api.post('update-profile', data),
  logout: () => api.post('logout'),
};

// Admins APIs
export const adminsAPI = {
  index: (params = {}) => api.get('admins', { params }),
  show: (id) => api.get(`admins/${id}`),
  store: (data) => api.post('create-admin', data),
  update: (id, data) => api.post(`update-admins/${id}`, data),
  delete: (id) => api.delete(`delete-admins/${id}`),
};

// Admin Invoices APIs
export const adminInvoicesAPI = {
  index: (params = {}) => api.get('admin-invoices', { params }),
  show: (id) => api.get(`admin-invoices/${id}`),
  view: (id) => api.get(`admin-invoices/view/${id}`),
  store: (data) => api.post('admin-invoices', data),
  update: (id, data) => api.post(`admin-invoices/${id}`, data),
  delete: (id) => api.delete(`admin-invoices/${id}`),
};

// Admin Logs APIs
export const adminLogsAPI = {
  index: (params = {}) => api.get('logs', { params }),
  delete: (id) => api.delete(`logs/${id}`),
};

// Admin Contact Message APIs
export const adminContactMessageAPI = {
  index: (params = {}) => api.get('contacts', { params }),
  delete: (id) => api.delete(`contacts/${id}`),
};

// Users APIs
export const usersAPI = {
  index: (params = {}) => api.get('users', { params }),
  show: (id) => api.get(`users/${id}`),
  update: (id, data) => api.post(`users/${id}`, data),
  delete: (id) => api.delete(`users/${id}`),
  confirm: (id) => api.post(`users/confirmed/${id}`),
};

// Drivers APIs
export const driversAPI = {
  all: () => api.get('all-drivers'),
  index: (params = {}) => api.get('drivers', { params }),
  approved: (params = {}) => api.get('drivers/approved', { params }),
  pending: (params = {}) => api.get('drivers/pending', { params }),
  show: (id) => api.get(`drivers/${id}`),
  update: (id, data) => api.post(`drivers/${id}`, data),
  delete: (id) => api.delete(`drivers/${id}`),
  toggleStatus: (id) => api.post(`drivers/toggle-driver/${id}`),
  emailChecked: (id) => api.post(`drivers/email-checked/${id}`),
  smsChecked: (id) => api.post(`drivers/sms-checked/${id}`),
  confirm: (id) => api.post(`drivers/confirmed/${id}`),
  updateDocumentExpiry: (driverId, documentId, data) => 
    api.post(`drivers/${driverId}/documents/${documentId}/expire-date`, data),
};

// Drivers Company APIs
export const driversCompanyAPI = {
  index: (params = {}) => api.get('drivers-company', { params }),
  show: (id) => api.get(`drivers-company/${id}`),
  store: (data) => api.post('drivers-company', data),
  update: (id, data) => api.post(`drivers-company/${id}`, data),
  toggleStatus: (id) => api.post(`drivers-company/toggle-driver/${id}`),
  delete: (id) => api.delete(`drivers-company/${id}`),
};

// Drivers Vehicles APIs
export const driversVehiclesAPI = {
  index: (params = {}) => api.get('drivers-vehicles', { params }),
  show: (id) => api.get(`drivers-vehicles/${id}`),
  getByDriver: (driverId, params = {}) => api.get(`vehicle-for-driver/${driverId}`, { params }),
  update: (id, data) => api.post(`drivers-vehicles/${id}`, data),
  delete: (id) => api.delete(`drivers-vehicles/${id}`),
};

// Special Bookings APIs
export const specialBookingsAPI = {
  index: (params = {}) => api.get('special-bookings', { params }),
  show: (id) => api.get(`special-bookings/${id}`),
  store: (data) => api.post('special-bookings', data),
  sendNote: (id, data) => api.post(`special-bookings/send-note/${id}`, data),
  assigned: () => api.get('special-bookings-assigned'),
  received: () => api.get('special-bookings-received'),
  completed: () => api.get('special-bookings-completed'),
  completedShow: (id) => api.get(`special-bookings-completed/${id}`),
  track: (bookingId) => api.get('special-bookings-track', { params: { booking_id: bookingId } }),
  delete: (id) => api.delete(`special-bookings/${id}`),
};

// Bookings APIs
export const bookingsAPI = {
  all: () => api.get('all-bookings'),
  index: (params = {}) => api.get('bookings', { params }),
  show: (id) => api.get(`bookings/${id}`),
  delete: (id) => api.delete(`bookings/${id}`),
  assignDriver: (id, data) => api.post(`bookings/assign-driver/${id}`, data),
  addDiscount: (id, data) => api.post(`bookings/${id}/discount`, data),
  markPaid: (id) => api.post(`bookings/paid/${id}`),
  sendNote: (id, data) => api.post(`bookings/send-note/${id}`, data),
  completed: (params = {}) => api.get('bookings/completed', { params }),
  regular: (params = {}) => api.get('bookings/regular', { params }),
  fastOrders: (params = {}) => api.get('bookings/fast-orders', { params }),
  fastOrderShow: (id) => api.get(`bookings/fast-orders/${id}`),
  setFastOrder: (id, data) => api.post(`bookings/${id}/fast-order`, data),
  setPending: (id) => api.post(`bookings/${id}/pending`),
  approve: (id) => api.post(`bookings/${id}/approve`),
  reject: (id, data) => api.post(`bookings/${id}/reject`, data),
  hidePrice: (id, data) => api.post(`bookings/${id}/hide-price`, data),
  approved: (params = {}) => api.get('bookings/approved', { params }),
  approvedShow: (id) => api.get(`bookings/approved/${id}`),
  cancelled: (params = {}) => api.get('bookings/cancelled', { params }),
  track: (bookingId) => api.get('bookings/track', { params: { booking_id: bookingId } }),
  liveTracking: (id) => api.get(`bookings/live-track/${id}`),
};

export const bookingInvoices = {
  index: (params = {}) => api.get('booking-invoices', { params }),
  show: (id) => api.get(`booking-invoices/${id}`),
  update: (id, data) => api.post(`booking-invoices/${id}`, data),
};

// Bookings Orders APIs
export const bookingOrdersAPI = {
  index: (params = {}) => api.get('bookings-orders', { params }),
  show: (id) => api.get(`bookings-orders/${id}`),
  regular: (params = {}) => api.get('bookings-orders/regular', { params }),
  fast: (params = {}) => api.get('bookings-orders/fast', { params }),
  pending: (params = {}) => api.get('bookings-orders/pending', { params }),
  assigned: (params = {}) => api.get('bookings-orders/assigned', { params }),
  received: (params = {}) => api.get('bookings-orders/received', { params }),
  completed: (params = {}) => api.get('bookings-orders/completed', { params }),
  byBooking: (bookingId, params = {}) => api.get(`bookings-orders/booking/${bookingId}`, { params }),
  assign: (id, data) => api.post(`bookings-orders/${id}/assign`, data),
  updatePrice: (id, data) => api.post(`bookings-orders/${id}/update-price`, data),
  updateVehicle: (id, customerId, data) => api.post(`bookings-orders/${id}/update-vehicle/${customerId}`, data),
  accept: (id) => api.post(`bookings-orders/${id}/accept`),
  reject: (id, data) => api.post(`bookings-orders/${id}/reject`, data),
  receive: (id, data) => api.post(`bookings-orders/${id}/receive`, data),
  complete: (id, data) => api.post(`bookings-orders/${id}/complete`, data),
  delete: (id) => api.delete(`bookings-orders/${id}`),
};

// Transports APIs
export const transportsAPI = {
  all: () => api.get('all-transports'),
  index: (params = {}) => api.get('transports', { params }),
  show: (id) => api.get(`transports/${id}`),
  completed: (params = {}) => api.get('transports/completed', { params }),
  cars: (params = {}) => api.get('transports/cars', { params }),
  furnitures: (params = {}) => api.get('transports/furnitures', { params }),
  store: (data) => api.post('transports', data),
  assignDriver: (id, data) => api.post(`transports/assign-driver/${id}`, data),
  addCommission: (id, data) => api.post(`transports/${id}/commission`, data),
  markPaid: (id) => api.post(`transports/paid/${id}`),
  approved: (params = {}) => api.get('transports/approved', { params }),
  setPending: (id) => api.post(`transports/${id}/pending`),
  approve: (id) => api.post(`transports/${id}/approve`),
  reject: (id, data) => api.post(`transports/${id}/reject`, data),
  delete: (id) => api.delete(`transports/${id}`),
  track: (transportId) => api.get('transports/track', { params: { transport_id: transportId } }),
  liveTracking: (id) => api.get(`transports/live-track/${id}`),
};

// Transport Orders APIs
export const transportOrdersAPI = {
  index: (params = {}) => api.get('transports-orders', { params }),
  show: (id) => api.get(`transports-orders/${id}`),
  cars: (params = {}) => api.get('transports-orders/cars', { params }),
  furnitures: (params = {}) => api.get('transports-orders/furnitures', { params }),
  pending: (params = {}) => api.get('transports-orders/pending', { params }),
  assigned: (params = {}) => api.get('transports-orders/assigned', { params }),
  received: (params = {}) => api.get('transports-orders/received', { params }),
  completed: (params = {}) => api.get('transports-orders/completed', { params }),
  byTransport: (transportId, params = {}) => api.get(`transports-orders/transport/${transportId}`, { params }),
  markPaid: (id) => api.post(`transports-orders/${id}/paid`),
  delete: (id) => api.delete(`transports-orders/${id}`),
  getMessages: (transportId, orderId, params = {}) => 
    api.get(`transports/${transportId}/orders/${orderId}/messages`, { params }),
};

// Chat Transport Orders APIs
export const chatTransportOrdersAPI = {
  getMessages: (transportId, orderId, params = {}) => 
    api.get(`transports/${transportId}/orders/${orderId}/messages`, { params }),
  sendMessage: (transportId, orderId, data) => 
    api.post(`transports/${transportId}/orders/${orderId}/messages`, data),
  markAsRead: (transportId, orderId) => 
    api.post(`transports/${transportId}/orders/${orderId}/messages/mark-read`),
};

// Chat APIs
export const chatAPI = {
  conversations: (params = {}) => api.get('chats/conversations', { params }),
  customerMessages: (customerId) => api.get(`chats/customerMessages/${customerId}`),
  unreadCounts: () => api.get('chats/unread-count'),
  markAsRead: (customerId) => api.post(`chats/mark-as-read/${customerId}`),
  send: (data) => api.post('chats/send', data),
  delete: (conversationId) => api.delete(`chats/delete/${conversationId}`),
};

// Pricing APIs
export const pricingAPI = {
  distances: {
    index: (params = {}) => api.get('pricing-distances', { params }),
    show: (id) => api.get(`pricing-distances/${id}`),
    store: (data) => api.post('pricing-distances', data),
    update: (id, data) => api.post(`pricing-distances/${id}`, data),
    delete: (id) => api.delete(`pricing-distances/${id}`),
  },
  groups: {
    index: (params = {}) => api.get('pricing-groups', { params }),
    show: (id) => api.get(`pricing-groups/${id}`),
    store: (data) => api.post('pricing-groups', data),
    update: (id, data) => api.post(`pricing-groups/${id}`, data),
    delete: (id) => api.delete(`pricing-groups/${id}`),
  },
  commissions: {
    index: (params = {}) => api.get('commissions', { params }),
    show: (id) => api.get(`commissions/${id}`),
    store: (data) => api.post('commissions', data),
    update: (id, data) => api.post(`commissions/${id}`, data),
    delete: (id) => api.delete(`commissions/${id}`),
  },
};

// calculator Area APIs
export const calculatorAreaAPI ={
  index: (params = {}) => api.get('calculator-areas', { params }),
  show: (id) => api.get(`calculator-areas/${id}`),
  store: (data) => api.post('calculator-areas', data),
  update: (id, data) => api.post(`calculator-areas/${id}`, data),
  delete: (id) => api.delete(`calculator-areas/${id}`),
};

// Contact APIs
export const contactAPI = {
  index: (params = {}) => api.get('contacts', { params }),
  show: (id) => api.get(`contacts/${id}`),
  store: (data) => api.post('contacts', data),
  delete: (id) => api.delete(`contacts/${id}`),
};

// Postal Area APIs
export const postalAreaAPI = {
  index: (params = {}) => api.get('postal-area', { params }),
  show: (id) => api.get(`postal-area/${id}`),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('settings'),
  update: (data) => api.post('settings/1', data),
};

// Social Media APIs
export const socialsAPI = {
  index: () => api.get('socials'),
  update: (id, data) => api.post(`socials/${id}`, data),
};

// Dashboard Counts API
export const dashboardAPI = {
  counts: () => api.get('counts'),
};